import { FC, useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';

import { BACKEND_URL } from '../config';
import { fetchAllData, fetchWithAuthMethod } from '../utils';

export const EditDeviceTypeChannels: FC = () => {
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [deviceTypes, setDeviceTypes] = useState<any[]>([]);
  const [sensorTypes, setSensorTypes] = useState<any[]>([]);
  const [sensorTypeBindings, setSensorTypeBindings] = useState<any[]>([]);
  const [selectedDeviceTypeId, setSelectedDeviceTypeId] = useState<string | null>(null); 
  const [deviceTypeMaxChannels, setDeviceTypeMaxChannels] = useState(0);
  const [deviceTypeChannelIds, setDeviceTypeChannelIds] = useState<any[]>([]);
  const [deviceTypeChannelId, setDeviceTypeChannelId] = useState<string | null>(null);
  const [sensorTypeId, setSensorTypeId] = useState<string | null>(null);
  const [selecteSensorTypeBindingId, setSelectedSensorTypeBindingId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [deletingSensorTypeBindingId, setDeletingSensorTypeBindingId] = useState(null);

  const fetchData = async (deviceTypeId: string | null = null) => {
    if (1 > deviceTypes.length) {
      return;
    }

    try {
      const deviceType = deviceTypes.find((type) => type.id === deviceTypeId);
      setDeviceTypeMaxChannels(deviceType.max_channels);

      const channelIdList = Array.from({ length: deviceType.max_channels }, (_, i) => ({
        value: (i + 1),
        label: (i + 1).toString(),
      }));
      setDeviceTypeChannelIds(channelIdList); 

      const responseData = await fetchAllData(`${BACKEND_URL}/sensor_type_bindings/by_device_type/${deviceTypeId}`);
      if (responseData.length === 0) {
        throw new Error('Failed to fetch sensor_type_bindings');
      }

      setSensorTypeBindings(responseData.map((x: any) => ({
        ...x,
        sensor_type_name: {
          label: x.sensor_type.name,
          value: x.sensor_type.id
        }
      })));
    } catch (error) {
      setSensorTypeBindings([]);
      console.error('Error fetching data:', error);
    }
  };

  const fetchDeviceTypes = async () => {
    try {
      // 端末種別を取得
      const responseData = await fetchAllData(`${BACKEND_URL}/device_types`);
      if (responseData.length === 0) {
        throw new Error('Failed to fetch device types');
      }

      // 端末種別をセット
      setDeviceTypes(responseData);

      // デフォルトの端末種別IDを設定
      if (responseData.length > 0) {
        const defaultDeviceTypeId = responseData[0].id;
        setSelectedDeviceTypeId(defaultDeviceTypeId);
        fetchData(defaultDeviceTypeId); // デフォルトの端末種別IDでデータを取得
      }    
    } catch (error) {
      console.error('Error fetching device types:', error);
    }
  };

  const fetchSensorTypes = async () => {
    try {
      // センサ種別を取得
      const responseData = await fetchAllData(`${BACKEND_URL}/sensor_types`);
      if (responseData.length === 0) {
        throw new Error('Failed to fetch sensor types');
      }

      const sensorTypesData = Array.isArray(responseData) ? responseData : [responseData];

      setSensorTypes(sensorTypesData.map((sensorType: any) => ({
        label: sensorType.name,
        value: sensorType.id,
      })));
    } catch (error) {
      console.error('Error fetching device types:', error);
    }
  };

  useEffect(() => {
    fetchSensorTypes();
    fetchDeviceTypes();
  }, []);

  useEffect(() => {
    if (selectedDeviceTypeId) {
      fetchData(selectedDeviceTypeId);
    }
  }, [selectedDeviceTypeId]);    

  const handleDeviceTypeChange = async (event: React.ChangeEvent<{ value: unknown }>) => {
    const deviceTypeId = event.target.value as string;
    setSelectedDeviceTypeId(deviceTypeId);

    await fetchData(deviceTypeId);
  };

  const handleEditClick = (row: any) => {
    setSelectedSensorTypeBindingId(row.id);
    setDeviceTypeChannelId(row.device_type_channel_id);
    setSensorTypeId(row.sensor_type_name ? row.sensor_type_name.value : '');
    setOpenEditDialog(true); // 編集ダイアログを開く

    setTimeout(() => {
      document.getElementById('device-type-channel-id-input')?.focus(); // IDを使ってフォーカス
    }, 0);
  };

  const handleEditDeviceTypeChannel = async () => {
    const confirmed = window.confirm('更新してもよろしいですか？');
    if (!confirmed) return; // ユーザーがキャンセルした場合は処理しない

    setIsLoading(true); // ローディング開始

    const response = await fetchWithAuthMethod(`${BACKEND_URL}/sensor_type_bindings/${selecteSensorTypeBindingId}`, `PUT`, {
      device_type_id: selectedDeviceTypeId,
      device_type_channel_id: deviceTypeChannelId,
      sensor_type_id: sensorTypeId
    });

    if (response.ok) {
      // センサ種別バインディング更新が成功した場合はセンサ種別バインディングリストを更新
      const updatedSensorTypeBinding = await response.json();
      // センサ種別バインディングリスト更新
      setSensorTypeBindings(prevSensorTypeBindings => {
        const newSensorTypeBindings = [...prevSensorTypeBindings];
        const index = newSensorTypeBindings.findIndex(sensor_type_binding =>sensor_type_binding.id === selecteSensorTypeBindingId);  
        if (0 <= index) {
          // 指定センサ種別バインディングのデータのみ更新
          newSensorTypeBindings[index] = {
            ...updatedSensorTypeBinding,
            sensor_type_name: {
              label: updatedSensorTypeBinding.sensor_type.name,
              value: updatedSensorTypeBinding.sensor_type.id
            }
          };
        }
        return newSensorTypeBindings;
      });

      handleCloseDialog('edit'); // 編集ダイアログを閉じる
    } else {
      const data = await response.json();
      if (data.detail.code === "SENSOR_TYPE_BINDING_EXISTS") {
        alert("指定されたセンサ種別チャネル設定は既に登録されています。");
      }
      
      console.error('Failed to update sensor_type_binding');
    }

    setIsLoading(false); // ローディング終了
  };

  const handleAddClick = () => {
    const usedChannelIds = sensorTypeBindings.map(binding => binding.device_type_channel_id);
    const newChannelId = Array.from({ length: deviceTypeMaxChannels }, (_, i) => (i + 1).toString())
                          .find((id) => !usedChannelIds.includes(Number(id))) || '';
    setDeviceTypeChannelId(newChannelId);
    setSelectedSensorTypeBindingId(newChannelId);

    const usedSensorTypeIds = sensorTypes.map(sensorType => sensorType.id);
    const newSensorTypeId = Array.from({ length: sensorTypes.length }, (_, i) => (i + 1).toString())
                          .find((id) => !usedSensorTypeIds.includes(Number(id))) || '';
    setSensorTypeId(newSensorTypeId);

    setOpenAddDialog(true); // 追加ダイアログを開く

    setTimeout(() => {
      document.getElementById('device-type-channel-id-input')?.focus(); // IDを使ってフォーカス
    }, 0);
  };

  const handleAddDeviceTypeChannel = async () => {
    // 確認メッセージを表示
    const confirmed = window.confirm('追加してもよろしいですか？');
    if (!confirmed) return; // ユーザーがキャンセルした場合は処理しない

    if (sensorTypeBindings.some(binding => binding.device_type_channel_id === deviceTypeChannelId)) {
      alert('登録済みのチャネル番号は追加できません');
      return;
    }

    setIsLoading(true); // ローディング開始

    const response = await fetchWithAuthMethod(`${BACKEND_URL}/sensor_type_bindings`, `POST`, {
      device_type_id: selectedDeviceTypeId,
      device_type_channel_id: deviceTypeChannelId,
      sensor_type_id: sensorTypeId,
    });

    if (response.ok) {
      const newBinding = await response.json();
      // チャネルリスト更新
      setSensorTypeBindings(prevBindings => {
        // 新しいチャネルの情報を準備
        const addBinding = {
          ...newBinding,
          sensor_type_name: {
            label: newBinding.sensor_type.name,
            value: newBinding.sensor_type.id
          }
        };

        return [...prevBindings, addBinding];
      });

      handleCloseDialog('add');      
    } else {
      const data = await response.json();
      if (data.detail.code === "SENSOR_TYPE_BINDING_EXISTS") {
        alert("指定されたセンサ種別チャネル設定は既に登録されています。");
      }

      console.error('Failed to add sensor_type_binding');
    }

    setIsLoading(false); // ローディング終了
  };

  const handleDeleteClick = async (row: any) => {
    // 確認メッセージを表示
    const confirmed = window.confirm('削除してもよろしいですか？');
    if (!confirmed) return;

    setDeletingSensorTypeBindingId(row.id);
    setIsLoading(true); // ローディング開始
     
    // センサ種別バインディング削除
    const response = await fetchWithAuthMethod(`${BACKEND_URL}/sensor_type_bindings/${row.id}`, `DELETE`);
    if (response.ok) {
      // 端末種別リスト更新
      setSensorTypeBindings(sensorTypeBindings.filter((sensor_type_binding: any) => sensor_type_binding.id !== row.id));
    } else {
      const data = await response.json();
      if (data.detail.code === "HAS_LINKED_SENSOR_TYPES") {
        alert("このセンサ種別が割り当てられている端末が存在するため、削除できません。");
      }

      console.error('Failed to delete sensor_type_binding');
    }

    setIsLoading(false); // ローディング終了
    setDeletingSensorTypeBindingId(null);
  };

  const handleCloseDialog = (dialogType: 'add' | 'edit', event?: React.SyntheticEvent<Element, Event> | {}, reason?: string) => {
    // 背景をクリックされるとダイアログの各値がクリアされてしまう対策
    if (reason && reason === 'backdropClick') {
      return;
    }

    setDeviceTypeChannelId('');
    setSensorTypeId('');

    if (dialogType === 'add') {
      setOpenAddDialog(false);
    } else if (dialogType === 'edit') {
      setOpenEditDialog(false);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'device_type_channel_id',
      headerName: 'チャネル番号',
      editable: false,
      width: 160,
      align: 'right',
    },
    {
      field: 'sensor_type_name',
      headerName: 'センサ種別名称',
      editable: false,
      width: 160,
      sortable: false,
      disableColumnMenu: true,
      valueGetter: (params) => params.row.sensor_type_name ? params.row.sensor_type_name.label : ''
    },
    {
      field: 'edit',
      headerName: '編集',
      width: 60,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <IconButton aria-label="edit" onClick={() => handleEditClick(params.row)}>
          <EditIcon />
        </IconButton>
      ),
    },
    {
      field: 'delete',
      headerName: '削除',
      width: 60,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <IconButton aria-label="delete" onClick={() => handleDeleteClick(params.row)}>
          {deletingSensorTypeBindingId === params.row.id ? (
            <CircularProgress size={24} />
          ) : (
            <DeleteIcon />
          )}
        </IconButton>
      ),
    }
  ];

  return (
    <Box sx={{ height: '60vh', width: '100%', marginTop: 2 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 250px)', marginBottom: 2 }} id="datalist-grid">
        {/* 端末種別のコンボボックス */}
        <TextField
          select
          margin="dense"
          label="端末種別"
          fullWidth
          value={selectedDeviceTypeId}
          onChange={handleDeviceTypeChange}
          InputLabelProps={{
            shrink: true, // ラベルを常に表示するためのプロパティ
          }}
          SelectProps={{
            MenuProps: {
              PaperProps: {
                style: {
                  width: 'auto', // ドロップダウンメニューの幅を自動調整
                  minWidth: '95%', // コンボボックスの幅に合わせる
                },
              },
            },
          }}
        >
          {deviceTypes.map((deviceType) => (
            <MenuItem key={deviceType.id} value={deviceType.id}>
              {deviceType.name}
            </MenuItem>
          ))}
        </TextField>

        {/* データグリッド */}
        <DataGrid
          rows={sensorTypeBindings}
          columns={columns}          
          pageSize={100} // 1ページの表示件数
          rowsPerPageOptions={[100]}
          disableSelectionOnClick
          experimentalFeatures={{ newEditingApi: true }}
          density="compact"
          sx={{ flexGrow: 1, overflowY: 'auto',
            "& .MuiDataGrid-cell:focus": {
              outline: "none",
            },
            "& .MuiDataGrid-cell:focus-within": {
              outline: "none",
            },
            "& .Mui-selected": {
              outline: "none",
            },
          }}
        />
      </Box>

      {/* 追加ボタン */}
      <IconButton
        aria-label="add"
        onClick={handleAddClick}
        disabled={sensorTypeBindings.length >= deviceTypeMaxChannels}
      >
        <AddIcon />
        <Typography variant="body1">追加</Typography>
      </IconButton>

      <Dialog open={openAddDialog} onClose={(event, reason) => handleCloseDialog('add', event || {}, reason)}>
        <DialogTitle>端末種別チャネル設定の追加</DialogTitle>
        <DialogContent>
          <TextField
            id="device-type-channel-id-input"
            autoFocus
            select
            margin="dense"
            label="チャネル番号"
            fullWidth
            value={deviceTypeChannelId}
            onChange={(e) => setDeviceTypeChannelId(e.target.value)}
          >
           {deviceTypeChannelIds.map((deviceTypeChannelId) => (
             <MenuItem key={deviceTypeChannelId.value} value={deviceTypeChannelId.value}>
               {deviceTypeChannelId.label}
             </MenuItem>
           ))}
          </TextField>
          <TextField
            select
            margin="dense"
            label="センサ種別名称"
            fullWidth
            value={sensorTypeId}
            onChange={(e) => setSensorTypeId(e.target.value)}
          >
            {sensorTypes.map((sensorType) => (
              <MenuItem key={sensorType.value} value={sensorType.value}>
                {sensorType.label}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleCloseDialog('add')}>キャンセル</Button>
          {isLoading ? (
            <CircularProgress /> // MUIのスピナーを表示
          ) : (
            <Button 
              onClick={handleAddDeviceTypeChannel}
            >
              追加
            </Button> 
          )}
        </DialogActions>
      </Dialog>
      <Dialog open={openEditDialog} onClose={(event, reason) => handleCloseDialog('edit', event || {}, reason)}>
        <DialogTitle>端末種別チャネル設定の編集</DialogTitle>
        <DialogContent>
          <TextField
            id="device-type-channel-id-input" 
            autoFocus
            select
            margin="dense"
            label="チャネル番号"
            fullWidth
            value={deviceTypeChannelId}
            onChange={(e) => setDeviceTypeChannelId(e.target.value)}
            disabled   // 編集ダイアログのときはdisabled設定にする
          >
           {deviceTypeChannelIds.map((deviceTypeChannelId) => (
             <MenuItem key={deviceTypeChannelId.value} value={deviceTypeChannelId.value}>
               {deviceTypeChannelId.label}
             </MenuItem>
           ))}
          </TextField>
          <TextField
            select
            margin="dense"
            label="センサ種別名称"
            fullWidth
            value={sensorTypeId}
           onChange={(e) => setSensorTypeId(e.target.value)}
          >
            {sensorTypes.map((sensorType) => (
              <MenuItem key={sensorType.value} value={sensorType.value}>
                {sensorType.label}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleCloseDialog('edit')}>キャンセル</Button>
          {isLoading ? (
            <CircularProgress /> // MUIのスピナーを表示
          ) : (
            <Button 
              onClick={handleEditDeviceTypeChannel}
            >
              更新
            </Button> 
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
